import { moduleSpec } from '@yourstore/domain';
import type { Messages, T } from '../../messages';

/**
 * **A feature's name** — the console's one table from a module key to words
 * (plan §1, principle 2: "Features", not modules). Setup's switches, the
 * Activity sentences and the partner-key dialog all read it, so a feature is
 * called the same thing wherever an operator meets it.
 *
 * Arrangement (which group a switch sits in, what turning it off takes away)
 * is Setup's (`setup/featureList.ts`); this file only names.
 *
 * **A module this build has no words for is still named**: by the domain's
 * own label, else by its key with spaces — never by nothing.
 */

/** A catalog key whose entry is a plain string. */
type Plain = { [K in keyof Messages]: Messages[K] extends string ? K : never }[keyof Messages];

const NAMES: Record<string, { title: Plain; said: Plain }> = {
  calendar: { title: 'feature.calendar', said: 'feature.calendar.said' },
  clients: { title: 'feature.clients', said: 'feature.clients.said' },
  services: { title: 'feature.services', said: 'feature.services.said' },
  sales: { title: 'feature.sales', said: 'feature.sales.said' },
  products: { title: 'feature.products', said: 'feature.products.said' },
  certificates: { title: 'feature.certificates', said: 'feature.certificates.said' },
  credit: { title: 'feature.credit', said: 'feature.credit.said' },
  reporting: { title: 'feature.reporting', said: 'feature.reporting.said' },
  billing: { title: 'feature.billing', said: 'feature.billing.said' },
  booking_site: { title: 'feature.booking_site', said: 'feature.booking_site.said' },
  notifications: { title: 'feature.notifications', said: 'feature.notifications.said' },
  partner_api: { title: 'feature.partner_api', said: 'feature.partner_api.said' },
  ordering: { title: 'feature.ordering', said: 'feature.ordering.said' },
  lodging: { title: 'feature.lodging', said: 'feature.lodging.said' },
  departures: { title: 'feature.departures', said: 'feature.departures.said' },
  photos: { title: 'feature.photos', said: 'feature.photos.said' },
  fulfillment: { title: 'feature.fulfillment', said: 'feature.fulfillment.said' },
  admin: { title: 'feature.admin', said: 'feature.admin.said' },
};

/** True when the console has its own words for this module. */
export function isNamedFeature(module: string): boolean {
  return Object.prototype.hasOwnProperty.call(NAMES, module);
}

/** The name a switch or a heading carries: "Gift cards". */
export function featureTitle(t: T, module: string): string {
  const names = NAMES[module];
  if (names !== undefined) return t(names.title);
  return moduleSpec(module)?.label ?? module.replace(/_/g, ' ');
}

/** The name in the middle of a sentence: "turned on gift cards", "Turn on the boutique first". */
export function featureName(t: T, module: string): string {
  const names = NAMES[module];
  if (names !== undefined) return t(names.said);
  return module.replace(/_/g, ' ');
}
